import React, { useEffect, useState } from 'react';
import { personalInfo, skills } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { Code, Palette, Heart, Mail, MapPin, Phone } from 'lucide-react';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero */}
      <section
        style={{
          padding: `${SPACING.xxxl} ${SPACING.lg}`,
          background: `radial-gradient(circle at 50% 50%, ${COLORS.primary}08 0%, transparent 60%)`,
        }}
      >
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING.xxl,
            alignItems: 'center',
          }}
          className="about-grid"
        >
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <div
              style={{
                display: 'inline-block',
                padding: `${SPACING.xs} ${SPACING.md}`,
                backgroundColor: `${COLORS.primary}15`,
                border: `1px solid ${COLORS.primary}40`,
                borderRadius: '50px',
                marginBottom: SPACING.md,
              }}
            >
              <span style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '500' }}>About Me</span>
            </div>
            <h1
              style={{
                fontSize: '56px',
                fontWeight: '700',
                color: COLORS.text.primary,
                marginBottom: SPACING.md,
              }}
            >
              Multi-Disciplinary Technologist
            </h1>
            <p
              style={{
                fontSize: '18px',
                lineHeight: '1.7',
                color: COLORS.text.secondary,
                marginBottom: SPACING.lg,
              }}
            >
              {personalInfo.bio}
            </p>
            <div style={{ display: 'flex', gap: SPACING.sm, flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                <MapPin size={20} color={COLORS.primary} />
                <span style={{ color: COLORS.text.secondary }}>{personalInfo.location}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                <Mail size={20} color={COLORS.primary} />
                <a
                  href={`mailto:${personalInfo.email}`}
                  style={{ color: COLORS.text.secondary, textDecoration: 'none' }}
                  onMouseEnter={(e) => (e.target.style.color = COLORS.primary)}
                  onMouseLeave={(e) => (e.target.style.color = COLORS.text.secondary)}
                >
                  {personalInfo.email}
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                <Phone size={20} color={COLORS.primary} />
                <span style={{ color: COLORS.text.secondary }}>{personalInfo.phone}</span>
              </div>
            </div>
          </div>

          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1) 100ms',
            }}
          >
            <div
              style={{
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: `0 20px 60px ${COLORS.primary}30`,
              }}
            >
              <img
                src={personalInfo.portraitImage}
                alt={personalInfo.name}
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  objectPosition: 'center -50px',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section style={{ padding: `${SPACING.xxxl} ${SPACING.lg}`, backgroundColor: COLORS.surface }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: '36px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.xl,
              textAlign: 'center',
            }}
          >
            Skills & Expertise
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: SPACING.lg,
            }}
          >
            {[
              { title: 'Technical Skills', icon: Code, items: skills.technical },
              { title: 'Creative Skills', icon: Palette, items: skills.creative },
              { title: 'Soft Skills', icon: Heart, items: skills.soft },
            ].map((category, index) => {
              const Icon = category.icon;
              return (
                <div
                  key={index}
                  style={{
                    padding: SPACING.lg,
                    backgroundColor: COLORS.cardSurface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '16px',
                    opacity: isVisible ? 1 : 0,
                    transition: `opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 100}ms`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.md }}>
                    <Icon size={24} color={COLORS.primary} />
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        color: COLORS.text.primary,
                      }}
                    >
                      {category.title}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.xs }}>
                    {category.items.map((skill, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: '14px',
                          color: COLORS.text.secondary,
                          backgroundColor: COLORS.surface,
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .about-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
